import { type ChatMessage } from '@autone/redux/agent';
import { useAutoneTranslation } from '@autone/translations';
import {
  type ForwardedRef,
  forwardRef,
  memo,
  type ReactNode,
  useEffect,
  useRef,
} from 'react';

import { Button } from '../../atoms';
import { CancelIcon, ChatIcon } from '../../icons';
import { cn } from '../../utils';

import { ChatComposer } from './ChatComposer';
import { Markdown } from './Markdown';

interface ChatPanelProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  onSend: (prompt: string) => void;
  onCancel: () => void;
  onClose: () => void;
  onResizeStart: (event: React.PointerEvent) => void;
  width: number;
}

export const ChatPanel = forwardRef(function ChatPanel(
  {
    messages,
    isStreaming,
    onSend,
    onCancel,
    onClose,
    onResizeStart,
    width,
  }: ChatPanelProps,
  ref: ForwardedRef<HTMLTextAreaElement>,
) {
  const { common } = useAutoneTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Only auto-scroll when the user is already near the bottom, so scrolling up
    // to re-read earlier messages isn't interrupted by incoming stream deltas.
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [messages]);

  return (
    <aside
      className="relative flex h-screen shrink-0 flex-col border-l border-border bg-background"
      style={{ width }}
      role="complementary"
      aria-label={common('chat.title')}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={common('chat.resize')}
        onPointerDown={onResizeStart}
        className="absolute inset-y-0 left-0 z-10 w-1 -translate-x-1/2 cursor-col-resize hover:bg-border"
      />

      <header className="flex shrink-0 items-center gap-2 border-b border-border bg-muted/30 px-3 py-2.5">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ChatIcon className="h-4 w-4" />
        </div>
        <h2 className="flex-1 truncate text-sm font-semibold">
          {common('chat.title')}
        </h2>
        <Button
          id="agent-chat-close"
          aria-label={common('chat.close')}
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          className="h-7 w-7 text-muted-foreground"
        >
          <CancelIcon className="h-3.5 w-3.5" />
        </Button>
      </header>

      <div
        ref={scrollRef}
        className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pt-4 pb-3"
      >
        {messages.map((m, i) => (
          <MessageBubble
            key={m.id}
            message={m}
            isStreaming={isStreaming && i === messages.length - 1}
          />
        ))}
      </div>

      <div className="shrink-0 border-t border-border bg-background/40 p-3">
        <ChatComposer
          ref={ref}
          variant="panel"
          autoFocus
          isStreaming={isStreaming}
          onSend={onSend}
          onCancel={onCancel}
        />
      </div>
    </aside>
  );
});

interface BubbleProps {
  className?: string;
  children: ReactNode;
}

const Bubble = ({ className, children }: BubbleProps) => (
  <div
    className={cn(
      'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm',
      className,
    )}
  >
    {children}
  </div>
);

interface MessageBubbleProps {
  message: ChatMessage;
  isStreaming: boolean;
}

const MessageBubble = memo(({ message, isStreaming }: MessageBubbleProps) => {
  const isUser = message.role === 'user';
  const showCursor = !isUser && isStreaming && message.content.length === 0;

  if (isUser) {
    return (
      <div className="flex justify-end">
        <Bubble className="rounded-br-sm bg-primary text-primary-foreground">
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
        </Bubble>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <Bubble className="rounded-bl-sm bg-muted text-foreground">
        {showCursor ? (
          <span className="inline-block h-3 w-2 animate-pulse bg-muted-foreground" />
        ) : (
          <Markdown source={message.content} />
        )}
      </Bubble>
    </div>
  );
});
MessageBubble.displayName = 'MessageBubble';
